import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CALL_STATUS, SALES_RESULT_VALUES } from '@autodialer/shared';

import { ApiError } from '../api/client';
import type { Campaign, CampaignField, Contact } from '../api/campaigns';
import { Alert, Button, Card, FormField, Input, Section, Select } from '../components';
import { useI18n, type TranslationKey } from '../i18n';
import { useSession } from '../auth/session-context';
import { PREVIEW_MODE, PREVIEW_TELEPHONY_HINT } from '../preview/preview-mode';
import { SipPhone, type PhoneState } from './sip-phone';
import { telephonyApi, type CallSummary } from './telephony-api';
import { useRealtime } from './useRealtime';

/**
 * Telefonieren aus einer Kampagne heraus (Phase 4: nur einzelne, von Hand
 * gestartete Gespräche).
 *
 * Bewusst schlicht: eine Nummer, ein großer Anrufen-Knopf, Gesprächszustand,
 * Dauer, Stummschalten, Auflegen. Die Gesprächsdauer wird aus dem
 * serverseitigen Verbindungszeitpunkt gerechnet - der Browser misst nichts,
 * was abgerechnet wird.
 */
export function CallPanel({
  campaign,
  contact,
  fields,
  onFinished,
}: {
  campaign: Campaign;
  contact: Contact | null;
  fields: CampaignField[];
  onFinished?: () => void;
}): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [phoneState, setPhoneState] = useState<PhoneState>('offline');
  const [phoneDetail, setPhoneDetail] = useState<string | undefined>();
  const [call, setCall] = useState<CallSummary | null>(null);
  const [manualNumber, setManualNumber] = useState('');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const phone = useRef<SipPhone | null>(null);

  // Telefon anmelden, solange dieser Bereich geöffnet ist.
  useEffect(() => {
    if (PREVIEW_MODE) {
      // Entwickler-Vorschau: das Telefon bleibt bewusst abgemeldet. Der
      // Bereich ist sichtbar, es wird aber keine Telefonie vorgetaeuscht.
      return;
    }

    const instance = new SipPhone({
      onState: (state, detail) => {
        setPhoneState(state);
        setPhoneDetail(detail);
      },
    });
    phone.current = instance;

    void (async () => {
      try {
        const { registration } = await telephonyApi.register();
        await instance.connect(registration);
      } catch (caught) {
        handleApiError(caught);
        setPhoneState('error');
      }
    })();

    return () => {
      void instance.disconnect();
      phone.current = null;
    };
  }, [handleApiError]);

  // Gesprächsdauer aus dem serverseitigen Verbindungszeitpunkt.
  useEffect(() => {
    if (!call?.connectedAt) {
      setSeconds(0);
      return;
    }
    const start = new Date(call.connectedAt).getTime();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [call?.connectedAt]);

  const refresh = useCallback(async (callId: string) => {
    const response = await telephonyApi.get(callId);
    setCall(response.call);
  }, []);

  useRealtime((event) => {
    const callId = event.payload.callId;
    if (typeof callId !== 'string') {
      return;
    }
    // Der Server ist die maßgebliche Stelle - nach jedem Ereignis wird der
    // Zustand dort abgeholt.
    void refresh(callId).catch(() => undefined);

    if (event.event === 'sip.error') {
      setError(t('call.providerProblem'));
    }
  });

  const run = async (action: () => Promise<void>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  const startCall = (): void => {
    void run(async () => {
      // Der Schlüssel verhindert, dass ein Doppelklick zwei Gespräche erzeugt.
      const clientRequestId = crypto.randomUUID();
      const response = await telephonyApi.start({
        campaignId: campaign.id,
        contactId: contact?.id ?? null,
        phoneNumber: contact ? null : manualNumber,
        clientRequestId,
      });
      setCall(response.call);
      setMuted(false);
    });
  };

  const hangup = (): void => {
    if (!call) {
      return;
    }
    void run(async () => {
      await telephonyApi.hangup(call.id);
      await refresh(call.id);
    });
  };

  const toggleMute = (): void => {
    if (!call) {
      return;
    }
    const next = !muted;
    setMuted(next);
    phone.current?.setMuted(next);
    void run(() => telephonyApi.mute(call.id, next).then(() => undefined));
  };

  const laeuft = call !== null && call.status !== CALL_STATUS.ENDED;
  const verbunden = call?.status === CALL_STATUS.CONNECTED;
  const nachbearbeitung = call?.status === CALL_STATUS.ENDED && call.requiresWrapUp;

  const statusText = useMemo<TranslationKey>(() => {
    if (!call) {
      return 'call.state.idle';
    }
    return ('call.state.' + call.status) as TranslationKey;
  }, [call]);

  return (
    <Card>
      <Section title={t('call.title')}>
      {PREVIEW_MODE ? <Alert tone="info">{PREVIEW_TELEPHONY_HINT}</Alert> : null}
      {/*
        Je Ursache eine eigene Meldung. Vorher hiess jeder Fehler
        "Mikrofon freigeben" - auch ein fehlendes Geraet, ein belegtes
        Mikrofon oder ein Problem der Telefonieebene.
      */}
      {phoneState === 'error' ? (
        <Alert tone="error">{t(meldungZu(phoneDetail))}</Alert>
      ) : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      {contact ? (
        <dl className="facts">
          <dt>{t('contact.phone')}</dt>
          <dd>{contact.phone}</dd>
          <dt>{t('contact.firstName')}</dt>
          <dd>{contact.firstName ?? '–'}</dd>
          <dt>{t('contact.lastName')}</dt>
          <dd>{contact.lastName ?? '–'}</dd>
        </dl>
      ) : (
        <FormField label={t('call.manualNumber')}>
          <Input type="tel" value={manualNumber} disabled={laeuft} onChange={setManualNumber} />
        </FormField>
      )}

      <p className="call-zustand">
        <span>{t(statusText)}</span>
        {verbunden ? <span className="call-dauer">{formatDuration(seconds)}</span> : null}
      </p>

      <div className="row-actions">
        {!laeuft ? (
          <Button
            variant="primary"
            size="gross"
            disabled={busy || phoneState !== 'ready' || (!contact && manualNumber.trim() === '')}
            onClick={startCall}
          >
            {t('call.start')}
          </Button>
        ) : (
          <>
            <Button onClick={toggleMute} disabled={!verbunden || busy}>
              {muted ? t('call.unmute') : t('call.mute')}
            </Button>
            <Button variant="danger" onClick={hangup} disabled={busy}>
              {t('call.hangup')}
            </Button>
          </>
        )}
      </div>

      {nachbearbeitung && call ? (
        <WrapUpForm
          call={call}
          fields={fields}
          onDone={() => {
            setCall(null);
            onFinished?.();
          }}
        />
      ) : null}

      {call?.status === CALL_STATUS.ENDED && !call.requiresWrapUp ? (
        <Button
          variant="primary"
          onClick={() => {
            setCall(null);
            onFinished?.();
          }}
        >
          {t('call.continue')}
        </Button>
      ) : null}
      </Section>
    </Card>
  );
}

/**
 * Nachbearbeitung nach einem verbundenen Gespräch mit Datensatz.
 *
 * Ohne Vertriebsergebnis geht es nicht weiter, und Pflichtfelder der Kampagne
 * müssen ausgefüllt sein (05_dialer.md). Erst der bewusste Klick auf
 * `Weiter telefonieren` macht den Caller wieder verfügbar - gemeinsam
 * genutzt vom manuellen Gespräch und vom Dialer.
 */
export function WrapUpForm({
  call,
  fields,
  onDone,
}: {
  call: CallSummary;
  fields: CampaignField[];
  onDone: () => void;
}): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [salesResult, setSalesResult] = useState('');
  const [note, setNote] = useState('');
  const [callbackDate, setCallbackDate] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pflichtfelder = fields.filter((field) => field.requiredBeforeCompletion);

  const submit = (): void => {
    void (async () => {
      setError(null);
      setBusy(true);
      try {
        await telephonyApi.wrapUp(call.id, {
          salesResult,
          note: note || null,
          callbackDate: callbackDate || null,
          fields: values,
        });
        onDone();
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="card">
      <h3>{t('call.wrapUp')}</h3>
      {error ? <Alert tone="error">{error}</Alert> : null}

      <FormField label={t('contact.status')}>
        <Select
          value={salesResult}
          options={[
            { value: '', label: '–' },
            ...SALES_RESULT_VALUES.map((result) => ({
              value: result as string,
              label: t(('status.' + result) as TranslationKey),
            })),
          ]}
          onChange={setSalesResult}
        />
      </FormField>

      {salesResult === 'callback' ? (
        <FormField label={t('contact.callbackDate')}>
          <Input type="date" value={callbackDate} onChange={setCallbackDate} />
        </FormField>
      ) : null}

      <FormField label={t('contact.note')}>
        <Input value={note} onChange={setNote} />
      </FormField>

      {pflichtfelder.map((field) => (
        <FormField key={field.id} label={field.fieldName} hint={t('contact.requiredBeforeCall')}>
          <Input
            value={values[field.id] ?? ''}
            onChange={(wert) => setValues({ ...values, [field.id]: wert })}
          />
        </FormField>
      ))}

      <Button variant="primary" disabled={busy || salesResult === ''} onClick={submit}>
        {t('call.continue')}
      </Button>
    </div>
  );
}

/** Anzeige der Gesprächsdauer, z. B. "02:15". */
export function formatDuration(seconds: number): string {
  const minuten = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const rest = (seconds % 60).toString().padStart(2, '0');
  return minuten + ':' + rest;
}

/**
 * Welche Meldung gehoert zu welchem Fehlergrund?
 *
 * Die Gruende kommen aus `telephony/microphone.ts` beziehungsweise von
 * `SipPhone`. Ein unbekannter Grund fuehrt zur allgemeinen Meldung - nie zu
 * einer falschen Behauptung ueber das Mikrofon.
 */
function meldungZu(detail: string | undefined): TranslationKey {
  switch (detail) {
    case 'microphone-denied':
      return 'call.microphoneDenied';
    case 'microphone-missing':
      return 'call.microphoneMissing';
    case 'microphone-busy':
      return 'call.microphoneBusy';
    case 'microphone-unavailable':
      return 'call.microphoneUnavailable';
    case 'microphone-error':
      return 'call.microphoneError';
    default:
      return 'call.phoneUnavailable';
  }
}
